import { useAuth } from '@clerk/nextjs';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';

// The QueryClient outlives page layouts, so its owner must survive their remounts.
const cacheOwners = new WeakMap<QueryClient, string>();

export function QueryIdentityBoundary({
  identity,
  children,
}: {
  identity: string | undefined;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [readyIdentity, setReadyIdentity] = useState(() =>
    cacheOwners.get(queryClient),
  );

  useEffect(() => {
    if (identity === undefined) return;

    if (cacheOwners.get(queryClient) !== identity) {
      // clear() also destroys pending queries, preventing late results from caching.
      queryClient.clear();
      cacheOwners.set(queryClient, identity);
    }
    setReadyIdentity(identity);
  }, [identity, queryClient]);

  if (
    identity === undefined ||
    readyIdentity !== identity ||
    cacheOwners.get(queryClient) !== identity
  ) {
    return null;
  }

  return children;
}

export function AuthQueryBoundary({ children }: { children: ReactNode }) {
  const { isLoaded, userId, sessionId, orgId } = useAuth();
  return (
    <QueryIdentityBoundary
      identity={
        isLoaded ? JSON.stringify([userId, sessionId, orgId]) : undefined
      }
    >
      {children}
    </QueryIdentityBoundary>
  );
}
